import React, { useState } from 'react';
import { auth, googleProvider } from './firebaseConfig';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';

function AuthForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // map Firebase error codes to friendly messages
  const friendlyError = (code) => {
    switch (code) {
      case 'auth/popup-closed-by-user':
        return 'You closed the sign‑in popup. Give it another try!';
      case 'auth/email-already-in-use':
        return 'That email is already registered. Please log in.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/wrong-password':
        return 'Incorrect password. Try again.';
      case 'auth/user-not-found':
        return "No account found with that email.";
      default:
        return `Oops, something went wrong. (${code})`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    try {
      if (isSignup) {
        const userCred = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCred.user);
        setMessage('✅ Account created! Check your email for a verification link before logging in.');
        setIsSignup(false);               // flip back to login
        setPassword('');                  // clear password
      } else {
        const userCred = await signInWithEmailAndPassword(auth, email, password);
        if (!userCred.user.emailVerified) {
          setError('❌ Please verify your email before continuing.');
          await auth.signOut();
        }
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setMessage('');
    setIsSubmitting(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error("Google Sign-In Error:", err.code, err.message);
      setError(`Login Failed: ${err.message} (${err.code})`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    setError('');
    setMessage('');
    if (!email) {
      setError('Please enter your email above to reset your password.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('✅ Password reset email sent. Check your inbox.');
      setResetRequested(false);
    } catch (err) {
      setError(friendlyError(err.code));
    }
  };

  return (
    <div className="w-full max-w-sm bg-white shadow-md rounded-lg p-6 mx-auto mt-6">
      <h2 className="text-lg font-semibold text-center mb-4">
        {resetRequested
          ? '🔑 Reset Password'
          : isSignup
            ? '📝 Create Account'
            : '🔐 Login'}
      </h2>

      {error && <p className="text-red-500 text-sm mb-3 text-center">{error}</p>}
      {message && <p className="text-green-600 text-sm mb-3 text-center">{message}</p>}

      {!resetRequested ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border px-3 py-2 rounded"
            />
          </div>

          {(isSignup || !resetRequested) && (
            <div>
              <label className="block text-sm font-medium">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border px-3 py-2 rounded"
              />
            </div>
          )}

          {!isSignup && (
            <div className="text-right -mt-2">
              <button
                type="button"
                className="text-sm text-indigo-600 hover:underline"
                onClick={() => {
                  setResetRequested(true);
                  setError('');
                  setMessage('');
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full text-white py-2 rounded transition mt-2 ${isSubmitting ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {isSubmitting ? 'Processing...' : (isSignup ? 'Create Account' : 'Login')}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-700 text-center">
            Enter your email and we’ll send a reset link.
          </p>
          <button
            onClick={handlePasswordReset}
            className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition"
          >
            Send Reset Email
          </button>
          <button
            onClick={() => setResetRequested(false)}
            className="w-full text-center text-sm text-gray-600 hover:underline mt-2"
          >
            Cancel
          </button>
        </div>
      )}

      {!resetRequested && (
        <>
          <div className="my-4 text-center text-sm text-gray-500">or</div>

          <button
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
            className={`w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 py-2 rounded transition ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          >
            <img src="/google-logo.png" alt="Google logo" className="w-5 h-5 mr-2" />
            Continue with Google
          </button>

          <p className="text-center mt-4 text-sm">
            {isSignup ? 'Already have an account?' : 'New to Clackbase?'}{' '}
            <button
              className="text-indigo-600 underline ml-1"
              onClick={() => {
                setIsSignup(!isSignup);
                setError('');
                setMessage('');
                setResetRequested(false);
              }}
            >
              {isSignup ? 'Login' : 'Sign up'}
            </button>
          </p>
        </>
      )}
    </div>
  );
}

export default AuthForm;
